import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FavoriteButton from "@/components/FavoriteButton";

export default function MyLibrary() {
  const { user, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  const isReader = !!(user && user.role === "reader");

  useEffect(() => {
    if (user === null) return; // still checking
    if (!isReader) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    api.get("/my/library")
      .then(({ data }) => { if (!cancelled) setBooks(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setBooks([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, isReader]);

  if (!isReader && user !== null) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="my-library-signin">
        <span
          className="inline-flex w-16 h-16 rounded-3xl items-center justify-center mb-6"
          style={{ background: "#FFE1EC", color: "#D6407E" }}
        >
          <Heart size={30} fill="#FF6B9D" stroke="#FF6B9D" />
        </span>
        <h1 className="font-fredoka text-4xl sm:text-5xl" style={{ color: "#2D3055" }}>
          Your little library awaits
        </h1>
        <p className="mt-4 font-nunito text-lg" style={{ color: "#4A4E6D" }}>
          Sign in with Google to heart your favourite books and keep them in one cozy place.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={signInWithGoogle}
            className="btn-terracotta"
            data-testid="my-library-signin-btn"
          >
            Sign in with Google
          </button>
          <button
            type="button"
            onClick={() => navigate("/books")}
            className="btn-ghost-warm"
          >
            Browse the books
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="my-library-page">
      <Link
        to="/books"
        className="inline-flex items-center gap-1 font-fredoka text-sm mb-6"
        style={{ color: "#6EC4E8" }}
      >
        <ArrowLeft size={16} /> Back to the library
      </Link>

      <span className="font-caveat text-3xl" style={{ color: "#FF6B9D" }}>your cozy corner</span>
      <h1 className="font-fredoka text-5xl sm:text-6xl" style={{ color: "#2D3055" }}>
        My Little Library
      </h1>
      <p className="mt-3 max-w-2xl text-lg" style={{ color: "#4A4E6D" }}>
        Every book you've hearted lives here — a small stack of favourites, always a tap away.
      </p>

      {loading && (
        <p className="mt-10 font-nunito" style={{ color: "#7E82A1" }}>Gathering your favourites…</p>
      )}

      {!loading && books.length === 0 && (
        <div className="mt-10 paper-card p-10 text-center" data-testid="my-library-empty">
          <p className="font-fredoka text-2xl" style={{ color: "#2D3055" }}>
            No hearted books yet
          </p>
          <p className="mt-2 font-nunito" style={{ color: "#4A4E6D" }}>
            Tap the little heart on any book to save it here.
          </p>
          <Link to="/books" className="btn-terracotta mt-6 inline-flex">
            Browse the books
          </Link>
        </div>
      )}

      {!loading && books.length > 0 && (
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {books.map((b, idx) => (
            <Link
              key={b.id}
              to={`/books/${b.id}`}
              className="warm-card p-5 fade-up book-tilt relative"
              style={{ animationDelay: `${idx * 80}ms` }}
              data-testid={`my-library-card-${b.id}`}
            >
              <div className="absolute top-3 left-3 z-10">
                <FavoriteButton bookId={b.id} variant="chip" />
              </div>
              <div className="rounded-2xl overflow-hidden mb-4 aspect-[3/4]">
                <img src={b.cover_image_url} alt={b.title} className="w-full h-full object-cover" />
              </div>
              <p className="font-caveat text-lg" style={{ color: "#FF6B9D" }}>{b.age_range}</p>
              <h3 className="font-fredoka text-2xl mt-1" style={{ color: "#2D3055" }}>{b.title}</h3>
              {b.subtitle && <p className="text-sm mt-1 font-caveat" style={{ color: "#6EC4E8" }}>{b.subtitle}</p>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
